import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isTooVague } from "./useTaskCoach";

interface ParentCoachResult {
  rewriteSuggestions: string[];
  hint: string;
  developmentAreas: string[];
  quotaExceeded?: boolean;
  quotaUsed?: number;
  quotaLimit?: number;
  coachingDisabled?: boolean;
}

export function useParentTaskCoach(childId: string | undefined, familyId: string | null) {
  const [coach, setCoach] = useState<ParentCoachResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [coachingDisabled, setCoachingDisabled] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTitleRef = useRef("");

  function check(title: string) {
    const trimmed = title.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (trimmed.length < 2 || !isTooVague(trimmed)) {
      setCoach(null);
      lastTitleRef.current = "";
      return;
    }

    if (quotaExceeded || coachingDisabled) return;
    if (trimmed === lastTitleRef.current) return;

    debounceRef.current = setTimeout(async () => {
      lastTitleRef.current = trimmed;
      setIsLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke("coach-task-parent", {
          body: { title: trimmed, childId, familyId },
        });
        if (error) throw error;
        const result = data as ParentCoachResult;

        if (result.coachingDisabled) {
          setCoachingDisabled(true);
          setCoach(null);
        } else if (result.quotaExceeded) {
          setQuotaExceeded(true);
          setCoach(null);
        } else {
          setCoach(result);
        }
      } catch (err) {
        console.warn("Parent task coach failed:", err);
        setCoach(null);
      } finally {
        setIsLoading(false);
      }
    }, 800);
  }

  function reset() {
    setCoach(null);
    lastTitleRef.current = "";
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return { coach, isLoading, check, reset, quotaExceeded, coachingDisabled };
}
